import React, { useState, useEffect } from 'react';
import { useCreateUser, useUpdateUser } from '../hooks/useApi';
import Modal from './ui/Modal';
import Button from './ui/Button';
import { FormField, FormGrid, Input, Select } from './ui/Form';

const FORM_ID = 'user-form';

const ROLES = [
    'admin',
    'Head of Department',
    'Manager',
    'Developer',
    'Officer',
    'Product Support',
    'Network Administrator',
    'Database Administrator',
    'System Administrator',
    'Cyber Security',
];

const DEPARTMENTS = [
'OPERATIONS',
'Finance',
'Human Resource and Admin',
'Internal Audit',
'ICT',
'Credit/nvestment',
'Risk and Compliance',
'Business',
'Public Relations',
'Chief Executive Office'
];

const UserModal = ({ user, onClose }) => {
    const createUser = useCreateUser();
    const updateUser = useUpdateUser();
    const isEditMode = !!user;

    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        role: 'employee',
        department: '',
        phone: '',
        is_active: true,
    });

    useEffect(() => {
        if (user) {
            setFormData({
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                email: user.email || '',
                password: '', // Password shouldn't be pre-filled
                role: user.role || 'employee',
                department: user.department || '',
                phone: user.phone || '',
                is_active: user.is_active !== undefined ? user.is_active : true,
            });
        }
    }, [user]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditMode) {
                await updateUser.mutateAsync({ id: user.id, data: formData });
            } else {
                await createUser.mutateAsync(formData);
            }
            onClose();
        } catch (error) {
            console.error('Error saving user:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const isLoading = createUser.isPending || updateUser.isPending;

    return (
        <Modal
            isOpen
            onClose={onClose}
            size="lg"
            title={isEditMode ? 'Edit user' : 'Add user'}
            subtitle={
                isEditMode
                    ? 'Change this person’s details, role or access.'
                    : 'Create an account and assign a role.'
            }
            footer={
                <>
                    <Button variant="secondary" onClick={onClose} disabled={isLoading}>
                        Cancel
                    </Button>
                    <Button type="submit" form={FORM_ID} variant="primary" loading={isLoading}>
                        {isEditMode ? 'Save changes' : 'Create user'}
                    </Button>
                </>
            }
        >
            <form id={FORM_ID} onSubmit={handleSubmit} className="space-y-4">
                <FormGrid>
                    <FormField label="First name" required htmlFor="user-first">
                        <Input
                            id="user-first"
                            name="first_name"
                            value={formData.first_name}
                            onChange={handleChange}
                            required
                        />
                    </FormField>

                    <FormField label="Last name" required htmlFor="user-last">
                        <Input
                            id="user-last"
                            name="last_name"
                            value={formData.last_name}
                            onChange={handleChange}
                            required
                        />
                    </FormField>
                </FormGrid>

                <FormGrid>
                    <FormField label="Email" required htmlFor="user-email">
                        <Input
                            id="user-email"
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="name@mybank.com"
                            autoComplete="email"
                        />
                    </FormField>

                    <FormField
                        label={isEditMode ? 'New password' : 'Password'}
                        required={!isEditMode}
                        htmlFor="user-password"
                        hint={isEditMode ? 'Leave blank to keep the current password.' : undefined}
                    >
                        <Input
                            id="user-password"
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            required={!isEditMode}
                            autoComplete="new-password"
                            placeholder={isEditMode ? '••••••••' : 'Set an initial password'}
                        />
                    </FormField>
                </FormGrid>

                <FormGrid cols={3}>
                    <FormField label="Role" required htmlFor="user-role">
                        <Select id="user-role" name="role" value={formData.role} onChange={handleChange}>
                            {ROLES.map((r) => (
                                <option key={r} value={r}>
                                    {r === 'admin' ? 'Admin' : r}
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField label="Department" htmlFor="user-department">
                        <Select
                            id="user-department"
                            name="department"
                            value={formData.department}
                            onChange={handleChange}
                        >
                            <option value="">Select department</option>
                            {DEPARTMENTS.map((d) => (
                                <option key={d} value={d}>
                                    {d}
                                </option>
                            ))}
                        </Select>
                    </FormField>

                    <FormField label="Phone" htmlFor="user-phone">
                        <Input
                            id="user-phone"
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="+252 …"
                        />
                    </FormField>
                </FormGrid>

                <label className="flex items-center gap-2 cursor-pointer select-none pt-1">
                    <input
                        type="checkbox"
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleChange}
                        className="w-3.5 h-3.5 accent-[var(--primary)]"
                    />
                    <span className="text-base text-ink-800">Account is active</span>
                    <span className="text-label text-ink-500">— inactive users cannot sign in</span>
                </label>
            </form>
        </Modal>
    );
};

export default UserModal;
